/**
 * The pieces of a two-dates stage card that Pomiary and Forma draw identically: the wait in the say
 * block, the sentence about A to B, the plot's geometry and its overlays, and the Trend and
 * Prognoza toggles with the instruction beside them.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReadoutPlotSpec } from '~/shared/ui/chart/ReadoutChart';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { MeasurementPoint } from '../model/measurements';
import type {
  MarkerKey,
  SelectionMove,
  TwoDatesMarker,
  TwoDatesProjection,
  TwoDatesSentence,
} from '../model/twoDates';

import styles from './DetailTab.module.css';
import { SelectionMarkers } from '~/shared/ui/chart/SelectionMarkers';
import { TwoDatesOverlay } from './TwoDatesOverlay';
import parts from './TwoDatesStageParts.module.css';

/**
 * The canon's plot geometry, in pixels. Numbers rather than tokens because bklit computes the inner
 * box from them in JavaScript. `right` widens when the forecast is on, because its end value is
 * labelled beside the last point and would otherwise hang off the box.
 */
const PLOT_MARGIN = { top: 30, right: 14, rightForecast: 84, bottom: 24, left: 44 } as const;
/** Four "nice" values, which is what the canon's gutter prints. */
const PLOT_Y_TICKS = 4;
/** The neutral line's weight on this plot. Heavier than a card's, because it IS the reading. */
const PLOT_STROKE = 2;

/** The plot both stages draw: month names, a bare y figure and no wash under the line. */
export function twoDatesPlot(projectionOn: boolean): ReadoutPlotSpec {
  return {
    margin: {
      top: PLOT_MARGIN.top,
      right: projectionOn ? PLOT_MARGIN.rightForecast : PLOT_MARGIN.right,
      bottom: PLOT_MARGIN.bottom,
      left: PLOT_MARGIN.left,
    },
    yTicks: PLOT_Y_TICKS,
    yTickStyle: 'bare',
    xTicks: 'months',
    area: false,
    strokeWidth: PLOT_STROKE,
  };
}

export type ChartAid = 'trend' | 'projection';

/** The toggles' handler over the two pieces of state the stage owns. */
export function toggleAid(
  setTrend: (flip: (on: boolean) => boolean) => void,
  setProjection: (flip: (on: boolean) => boolean) => void,
): (which: ChartAid) => void {
  return (which) => {
    (which === 'trend' ? setTrend : setProjection)((on) => !on);
  };
}

/** The say block's wait: the reading and the sentence as bars, and the words for a listener. */
export function TwoDatesSayWait({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
      {skeleton ? (
        <>
          <i className={`${String(skel.bar)} ${String(styles.skelNow)}`} />
          <i className={`${String(skel.bar)} ${String(styles.skelSentence)}`} />
        </>
      ) : null}
    </>
  );
}

export function TwoDatesSentenceText({
  sentence,
  className = '',
}: {
  readonly sentence: TwoDatesSentence;
  readonly className?: string;
}) {
  const { t } = useTranslation();
  return (
    <p className={`${String(styles.sentence)} ${className}`}>
      {sentence.from}{' '}
      <em className={styles.sentenceWord}>{t('web:clientDetail.body.sentenceTo')}</em>{' '}
      {sentence.to}:{' '}
      <span className={sentence.changeIsGood ? styles.sentenceGood : undefined}>
        {sentence.change}
      </span>{' '}
      <em className={styles.sentenceWord}>{sentence.span}</em>
    </p>
  );
}

interface TwoDatesPlotLayersProps {
  readonly view: {
    readonly readings: readonly MeasurementPoint[];
    readonly trend: readonly MeasurementPoint[] | null;
    readonly projection: TwoDatesProjection | null;
    readonly markers: readonly [TwoDatesMarker, TwoDatesMarker] | null;
  };
  readonly nameOf: (marker: MarkerKey) => string;
  readonly onMove: (move: SelectionMove) => void;
  readonly reducedMotion: boolean;
}

/** The selection painted over the line, the forecast, and the two draggable markers. */
export function TwoDatesPlotLayers({ view, nameOf, onMove, reducedMotion }: TwoDatesPlotLayersProps) {
  return (
    <>
      <TwoDatesOverlay
        line={view.trend ?? view.readings}
        projection={view.projection}
        reducedMotion={reducedMotion}
        selection={
          view.markers === null
            ? null
            : { a: view.markers[0].at.getTime(), b: view.markers[1].at.getTime() }
        }
        strokeLine={view.trend !== null}
      />
      {view.markers === null ? null : (
        <SelectionMarkers markers={view.markers} nameOf={nameOf} onMove={onMove} />
      )}
    </>
  );
}

// bklit places a chart child by its own type, and both layers stand above the series.
(TwoDatesPlotLayers as { __isPostOverlay?: boolean }).__isPostOverlay = true;

interface TwoDatesTogglesProps {
  readonly trend: boolean;
  readonly projection: boolean;
  readonly trendAvailable: boolean;
  readonly projectionAvailable: boolean;
  /** A wait is not a refusal: while nothing has answered, both toggles read available. */
  readonly answered: boolean;
  readonly onToggle: (which: ChartAid) => void;
  readonly instruction: string;
  /** Why each aid is refused, in the tab's own noun — measurements on Pomiary, sessions on Forma. */
  readonly refusals: { readonly trend: string; readonly projection: string };
  /** Forma's narrower stage: the refusals take a line of their own and the instruction wraps. */
  readonly layout?: 'compact';
}

const TOGGLES = ['trend', 'projection'] as const;

/**
 * A toggle the history cannot answer does not claim to be on: it stays focusable, reads
 * `aria-disabled` rather than pressed, and carries its reason as a visible caption tied with
 * `aria-describedby`.
 */
export function TwoDatesToggles({
  trend,
  projection,
  trendAvailable,
  projectionAvailable,
  answered,
  onToggle,
  instruction,
  refusals,
  layout,
}: TwoDatesTogglesProps) {
  const { t } = useTranslation();
  const ids = useId();
  const availableOf = (key: ChartAid) =>
    !answered || (key === 'trend' ? trendAvailable : projectionAvailable);

  return (
    <div className={`${String(parts.toggleRow)} ${layout === 'compact' ? String(parts.compact) : ''}`}>
      {TOGGLES.map((toggle) => {
        const on = toggle === 'trend' ? trend : projection;
        const available = availableOf(toggle);
        return (
          <button
            aria-describedby={available ? undefined : `${ids}-${toggle}`}
            aria-disabled={available ? undefined : true}
            aria-pressed={available && on}
            className={`${String(styles.toggle)} ${available && on ? String(styles.toggleOn) : ''} ${available ? '' : String(styles.toggleOff)}`}
            key={toggle}
            onClick={() => {
              if (available) onToggle(toggle);
            }}
            type="button"
          >
            <i aria-hidden="true" className={styles.toggleDot} />
            {t(`web:clientDetail.body.${toggle}`)}
          </button>
        );
      })}
      <span className={parts.reasons}>
        {TOGGLES.filter((toggle) => !availableOf(toggle)).map((toggle) => (
          <em
            className={`${String(styles.toggleReason)} ${String(parts.reason)}`}
            id={`${ids}-${toggle}`}
            key={toggle}
          >
            {refusals[toggle]}
          </em>
        ))}
      </span>
      <em className={`${String(styles.caption)} ${String(parts.instruction)}`}>{instruction}</em>
    </div>
  );
}
